import React, { forwardRef } from 'react'
import { MultiInputProps } from './MultiInput.types'
import {
  MultiInputContainer,
  MultiInputTag,
  MultiInputTagRemove,
  MultiInputField,
  MultiInputIconWrapper,
} from './MultiInput.styles'
import { Icon } from '../Icon'

export const MultiInput = forwardRef<HTMLInputElement, MultiInputProps>(
  (
    {
      tags = [],
      onTagRemove,
      maxVisibleTags = 3,
      overflowText = (count: number) => `+${count} more`,
      tagContent,
      value = '',
      onChange,
      placeholder,
      size = 'medium',
      variant = 'default',
      error = false,
      fullWidth = false,
      disabled = false,
      leftIcon,
      rightIcon,
      className,
      ...props
    },
    ref
  ) => {
    const displayTags = tags.slice(0, maxVisibleTags)
    const overflowCount = tags.length - maxVisibleTags
    const hasLeftIcon = !!leftIcon
    const hasRightIcon = !!rightIcon
    const showPlaceholder = tags.length === 0 && !value

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value)
    }

    const handleTagRemove = (tag: (typeof tags)[0]) => {
      if (tag.removable !== false) {
        onTagRemove?.(tag)
      }
    }

    const handleContainerClick = () => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.focus()
      }
    }

    return (
      <div
        id="multi-input-container"
        style={{
          position: 'relative',
          display: fullWidth ? 'block' : 'inline-block',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <MultiInputContainer
          id="multi-input"
          $size={size}
          $variant={variant}
          $error={error}
          $hasLeftIcon={hasLeftIcon}
          $hasRightIcon={hasRightIcon}
          $fullWidth={fullWidth}
          onClick={handleContainerClick}
          className={className}
        >
          {displayTags.map((tag) => (
            <MultiInputTag key={tag.id} $size={size}>
              {tagContent ? (
                tagContent(tag)
              ) : (
                <span title={tag.label}>{tag.label}</span>
              )}
              {tag.removable !== false && (
                <MultiInputTagRemove
                  onClick={(e) => {
                    e.stopPropagation()
                    handleTagRemove(tag)
                  }}
                  type="button"
                  aria-label={`Remove ${tag.label}`}
                >
                  <Icon name="x" size="small" />
                </MultiInputTagRemove>
              )}
            </MultiInputTag>
          ))}

          {overflowCount > 0 && (
            <MultiInputTag $size={size}>
              <span>{overflowText(overflowCount)}</span>
            </MultiInputTag>
          )}

          <MultiInputField
            id="multi-input-field"
            ref={ref}
            value={value}
            onChange={handleInputChange}
            placeholder={showPlaceholder ? placeholder : ''}
            disabled={disabled}
            $size={size}
            {...props}
          />
        </MultiInputContainer>

        {hasLeftIcon && (
          <MultiInputIconWrapper $position="left" $size={size}>
            {leftIcon}
          </MultiInputIconWrapper>
        )}

        {hasRightIcon && (
          <MultiInputIconWrapper $position="right" $size={size}>
            {rightIcon}
          </MultiInputIconWrapper>
        )}
      </div>
    )
  }
)

MultiInput.displayName = 'MultiInput'
